import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { ConfigurationService } from '@sondermind/configuration';
import { HttpService } from '@sondermind/http';
import {
  IAPIListResponse,
  IAPIListRequestOptions
} from '@sondermind/utilities/models-http';

import {
  ClientReferralLite,
  IClientReferralLiteApiResponse
} from '@sondermind/data-access/client-referral';

@Injectable({
  providedIn: 'root'
})
export class ClientPortalReferralsHttpService {
  constructor(
    private configurationService: ConfigurationService,
    private http: HttpService
  ) {}

  /**
   * Fetches all client portal referrals with automatic pagination
   * @param opts Optional request options
   * @returns Observable of all ClientReferralLites across all pages
   */
  fetchAll(opts: Partial<IAPIListRequestOptions> = {}): Observable<ClientReferralLite[]> {
    return this.fetchPage(opts).pipe(
      expand((response) => (response.meta.links.next
        ? this.fetchPageByUrl(response.meta.links.next)
        : EMPTY)
      ),
      // Collect all pages and flatten the data arrays
      reduce((allReferrals: ClientReferralLite[], response) => allReferrals.concat(response.data), [])
    );
  }

  /**
   * Fetches a single page of client portal referrals
   * @param opts Optional request options
   * @returns Observable of IAPIListResponse with ClientReferralLites
   */
  fetchPage(opts: Partial<IAPIListRequestOptions> = {}): Observable<IAPIListResponse<ClientReferralLite>> {
    const params = this.http.generateListParams(opts);
    const url = `${this.configurationService.env.svcBase}/referrals/api/v1/client_referrals`;

    return this.http
      .get<IAPIListResponse<IClientReferralLiteApiResponse>>(url, params)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((item) => new ClientReferralLite(item))
        })),
        this.http.handleHttpErrorStrict()
      );
  }

  /**
   * Fetches a page by direct URL (used for pagination)
   * @param url The full URL to fetch
   * @returns Observable of IAPIListResponse with ClientReferralLites
   */
  private fetchPageByUrl(url: string): Observable<IAPIListResponse<ClientReferralLite>> {
    return this.http
      .get<IAPIListResponse<IClientReferralLiteApiResponse>>(url)
      .pipe(
        map((resp) => ({
          ...resp,
          data: resp.data.map((item) => new ClientReferralLite(item))
        })),
        this.http.handleHttpErrorStrict()
      );
  }
}
